import { Text } from '@/components/text';
import { Pressable, View } from '@/components/ui/tw-wrappers';
import { SCREEN_MAX_WIDTH } from '@/constants/layout';
import { EMAIL_CONTACT, MENU_ITEMS } from '@/constants/navigation';
import { Colors } from '@/constants/theme';
import { openEmailContact } from '@/utils/mail';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet, TouchableOpacity } from 'react-native';

const FOOTER_LINKS = {
  Company: ['About Us', 'Careers', 'Blog', 'Contact', 'Partners'],
  Contact: {
    address: '123 Construction Avenue, Building District, City 10001',
    phone: '+1 (555) 123-4567',
    email: EMAIL_CONTACT,
  },
};

export function Footer() {
  const { t } = useTranslation();
  const router = useRouter();

  const openLink = (url: string) => {
    Linking.openURL(url);
  };

  return (
    <View style={styles.container}>
      <View style={styles.content} className='px-6 md:px-10'>
        {/* Top Section */}
        <View className='flex-col gap-10 mb-5 md:flex-row md:justify-between md:gap-12'>
          {/* Brand Column */}
          <View style={styles.brandColumn}>
            <View style={styles.logoContainer}>
              <Image
                source={require('@/assets/images/icon.png')}
                style={styles.logo}
                contentFit='contain'
                alt={t('common.logoAlt', 'PangeX Logo')}
              />
              <View className='flex-col justify-center '>
                <Text type='title' style={styles.headerTitleDesktop}>
                  {t('header.title')}
                </Text>
                <Text type='defaultBold' style={styles.by}>{t('footer.by', "by AlsoThere")}</Text>
              </View>
            </View>
            <Text style={styles.brandTagline}>{t('footer.tagline')}</Text>
            <View style={styles.socialRow}>
              <Pressable
                onPress={() => {
                  openLink('https://x.com/esourcecapital');
                }}
                style={styles.socialIcon}>
                <Ionicons name='logo-x' size={20} color={Colors.light.icon} />
              </Pressable>
              <Pressable
                onPress={() => {
                  openLink('https://www.linkedin.com/company/alsothere/');
                }}
                style={styles.socialIcon}>
                <Ionicons name='logo-linkedin' size={20} color={Colors.light.icon} />
              </Pressable>
            </View>
          </View>

          {/* Links Section */}
          <View className='flex-1 flex-row gap-8 flex-wrap md:flex-1 md:flex-row md:justify-end'>
            {/* Platform Column */}
            <View style={styles.linkColumn}>
              <Text type='defaultSemiBold' style={styles.columnHeader}>
                {t('footer.platform', 'Menu')}
              </Text>
              <View style={styles.linkList}>
                {MENU_ITEMS.map(item => (
                  <Pressable
                    key={item.key}
                    onPress={() => {
                      if (item.path.startsWith('http')) {
                        Linking.openURL(item.path);
                      } else if (item.path.startsWith('mailto:')) {
                        openEmailContact();
                      } else {
                        router.push(item.path as any);
                      }
                    }}>
                    <Text style={styles.linkText}>{t(item.key)}</Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Contact Column */}
            <View style={styles.linkColumn}>
              <Text type='defaultSemiBold' style={styles.columnHeader}>
                {t('footer.contact', 'Contact Info')}
              </Text>
              <View style={styles.linkList}>
                {/* <View style={styles.contactItem}>
                        <Ionicons name="location-outline" size={18} color={Colors.light.icon} />
                        <Text style={styles.linkText}>{FOOTER_LINKS.Contact.address}</Text>
                    </View> */}
                {/* <View style={styles.contactItem}>
                        <Ionicons name='call-outline' size={18} color={Colors.light.icon} />
                        <Text style={styles.linkText}>{FOOTER_LINKS.Contact.phone}</Text>
                    </View> */}
                <View style={styles.contactItem}>
                  <Ionicons name='mail-outline' size={18} color={Colors.light.icon} />
                  <Text style={styles.linkText}>{FOOTER_LINKS.Contact.email}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Bottom Section */}
        <View className='flex-col-reverse gap-4 items-start md:flex-row md:justify-between md:items-center'>
          <Text style={styles.copyright}>
            {t('footer.rights', '© {{year}} PangeX. All rights reserved.', {
              year: new Date().getFullYear(),
            })}
          </Text>
          <TouchableOpacity onPress={() => { Linking.openURL('https://esourcecapital.com/') }}>
            <Image
              source={require('@/assets/images/esource.png')}
              style={styles.esourceLogo}
              contentFit='contain'
              alt={t('common.logoAlt', 'ESource Capital Logo')}
            />
          </TouchableOpacity>

          {/* <View style={styles.bottomLinks}>
            <Pressable onPress={() => {}}>
              <Text style={styles.bottomLinkText}>{t('footer.privacy', 'Privacy')}</Text>
            </Pressable>
            <Pressable onPress={() => {}}>
              <Text style={styles.bottomLinkText}>{t('footer.terms', 'Terms')}</Text>
            </Pressable>
            <Pressable onPress={() => {}}>
              <Text style={styles.bottomLinkText}>{t('footer.sitemap', 'Sitemap')}</Text>
            </Pressable>
          </View> */}
        </View>
      </View>
    </View >
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#fff', // Or slightly off-white like #F9FAFB
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    paddingVertical: 20,
  },
  content: {
    width: '100%',
    maxWidth: SCREEN_MAX_WIDTH,
  },
  brandColumn: {
    maxWidth: 320,
    gap: 16,
  },
  logo: {
    width: 40,
    height: 40,
  },
  esourceLogo: {
    width: 120,
    height: 40,
  },
  brandTagline: {
    color: Colors.light.icon,
    lineHeight: 24,
  },
  socialRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  socialIcon: {
    opacity: 0.8,
  },
  linkColumn: {
    minWidth: 140,
    gap: 16,
  },
  columnHeader: {
    fontSize: 14,
    color: Colors.light.text,
  },
  linkList: {
    gap: 12,
    maxWidth: 240,
  },
  contactItem: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  linkText: {
    color: Colors.light.icon,
    fontSize: 14,
  },
  linkTextHover: {
    color: Colors.light.tint,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(0,0,0,0.06)',
    marginBottom: 20,
  },
  copyright: {
    color: Colors.light.icon,
    fontSize: 14,
  },
  bottomLinks: {
    flexDirection: 'row',
    gap: 24,
  },
  bottomLinkText: {
    color: Colors.light.icon,
    fontSize: 14,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  headerTitleDesktop: {
    fontSize: 18,
    lineHeight: 20
  },
  by: {
    color: '#3A5783',
    fontSize: 12,
    lineHeight: 14
  }
});
